import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, firstValueFrom, map, of, switchMap, tap } from 'rxjs';
import { Me } from './auth.models';
import { AuthStore } from './auth.store';

/**
 * Sessione e CSRF lato interfaccia. Gli indirizzi sono relativi: Angular aggiunge
 * l'intestazione `X-XSRF-TOKEN` solo alle richieste verso la stessa origine.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(AuthStore);

  /** Emette o rinnova il cookie `XSRF-TOKEN`. */
  refreshCsrf(): Observable<void> {
    return this.http.get('/api/auth/csrf', { responseType: 'text' }).pipe(map(() => undefined));
  }

  /** L'utente della sessione, o `null` se non c'è una sessione valida. */
  me(): Observable<Me | null> {
    return this.http.get<Me>('/api/auth/me').pipe(
      tap((user) => this.store.set(user)),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          this.store.clear();
          return of(null);
        }
        throw error;
      }),
    );
  }

  /**
   * Avvio dell'applicazione: prima il CSRF, poi l'utente. Un servizio non raggiungibile non
   * blocca l'avvio: l'interfaccia parte come anonima e le pagine mostrano l'errore.
   */
  async bootstrap(): Promise<void> {
    try {
      await firstValueFrom(this.refreshCsrf());
    } catch {
      // Senza cookie CSRF le richieste di modifica falliranno con un messaggio chiaro.
    }
    try {
      await firstValueFrom(this.me());
    } catch {
      this.store.clear();
    }
  }

  /** Dopo un accesso riuscito: utente nello stato e nuovo cookie CSRF legato alla sessione. */
  signedIn(user: Me): Observable<Me> {
    this.store.set(user);
    return this.refreshCsrf().pipe(
      map(() => user),
      catchError(() => of(user)),
    );
  }

  /** Chiude la sessione anche sul server, svuota lo stato e rinnova il CSRF (FR-011). */
  logout(): Observable<void> {
    return this.http.post('/api/auth/logout', null).pipe(
      catchError(() => of(null)),
      tap(() => this.clear()),
      switchMap(() => this.refreshCsrf().pipe(catchError(() => of(undefined)))),
      map(() => undefined),
    );
  }

  clear(): void {
    this.store.clear();
  }
}
